'use client'

import * as React from 'react'
import { User } from 'firebase/auth'
import { onIdTokenChange, getIdToken, getRecentSearches, getUserPreferences, onSubscriptionChange } from '@/lib/firebase'
import { beginCommuteSync, endCommuteSync } from '@/components/commute/sync'
import { api } from '@/lib/api'
import { useUserStore, useSettingsStore } from '@/lib/store'
import { DEFAULT_SUBSCRIPTION } from '@/lib/types'
import { AuthDialog } from './auth-dialog'

interface AuthContextType {
  user: User | null
  loading: boolean
  /** Opens the auth dialog; optionally on a specific tab. */
  signIn: (mode?: 'sign-in' | 'sign-up') => void
  signOut: () => Promise<void>
}

const AuthContext = React.createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [authDialogOpen, setAuthDialogOpen] = React.useState(false)
  const [authDialogMode, setAuthDialogMode] = React.useState<'sign-in' | 'sign-up'>('sign-in')
  
  React.useEffect(() => {
    const unsubscribe = onIdTokenChange(async (firebaseUser) => {
      // Update API client with auth token BEFORE setting user
      // This prevents race conditions where components see user but token isn't set
      if (firebaseUser) {
        // Pass the user directly to avoid auth.currentUser timing issues
        const token = await getIdToken(false, firebaseUser)
        api.setToken(token)
        api.setTokenRefresher(() => getIdToken(true, firebaseUser))
      } else {
        api.setToken(null)
        api.setTokenRefresher(null)
        // Reset subscription so stale Penthouse state doesn't bypass premium gates
        useSettingsStore.getState().setSubscription({ ...DEFAULT_SUBSCRIPTION })
      }
      
      setUser(firebaseUser)
      setLoading(false)

      // Server-readable auth hint (see app/page.tsx): lets the `/` route
      // SSR the signed-in layout instead of flashing the gate's teaser
      // listings for the ~½s Firebase needs to restore the session.
      // Not a credential — just "this browser had a session".
      try {
        document.cookie = firebaseUser
          ? 'roostr_authed=1; path=/; max-age=31536000; SameSite=Lax'
          : 'roostr_authed=; path=/; max-age=0; SameSite=Lax'
      } catch {
        // cookie write can fail in exotic embeds; the gate flash is the
        // graceful fallback
      }
    })
    
    return () => unsubscribe()
  }, [])
  
  // Sync recent searches and user preferences from Firestore on sign-in
  React.useEffect(() => {
    if (!user) {
      // Signed out — stop mirroring navigation targets to the account.
      endCommuteSync()
      return
    }

    const syncUserData = async () => {
      try {
        // Sync recent searches
        const firestoreSearches = await getRecentSearches(user.uid)
        if (firestoreSearches.length > 0) {
          useUserStore.getState().setRecentSearches(firestoreSearches)
        }

        // Sync user preferences into settings store
        const prefs = await getUserPreferences(user.uid)
        useSettingsStore.getState().hydrateFromFirebase({
          cardDensity: prefs.cardDensity,
          defaultViewMode: prefs.defaultViewMode,
          defaultCity: prefs.defaultCity,
          defaultSort: prefs.defaultSort,
          defaultPriceMode: prefs.defaultPriceMode,
          listingFreshness: prefs.listingFreshness,
          dealbreakers: prefs.dealbreakers,
          aiSummaryLanguage: prefs.aiSummaryLanguage,
          notifications: prefs.notifications,
          searchHistoryEnabled: prefs.searchHistoryEnabled,
          subscription: prefs.subscription,
        })

        // Adopt the account's navigation targets (or seed them from local).
        await beginCommuteSync(user.uid)
      } catch (error) {
        console.error('Failed to sync user data:', error)
      }
    }

    syncUserData()
  }, [user])

  // Real-time listener for subscription changes (e.g. after Stripe webhook)
  React.useEffect(() => {
    if (!user) return

    const unsubscribe = onSubscriptionChange(user.uid, (subscription) => {
      useSettingsStore.getState().setSubscription(subscription)
    })

    return () => unsubscribe()
  }, [user])

  // Refresh token periodically (Firebase tokens expire after ~1 hour)
  React.useEffect(() => {
    if (!user) return
    
    const refreshToken = async () => {
      try {
        const token = await getIdToken(true, user) // Force refresh
        api.setToken(token)
      } catch (error) {
        console.error('Failed to refresh token:', error)
      }
    }
    
    // Refresh every 50 minutes (before 1 hour expiry)
    const interval = setInterval(refreshToken, 50 * 60 * 1000)

    const refreshOnReturn = () => {
      if (document.visibilityState === 'visible') {
        void refreshToken()
      }
    }
    document.addEventListener('visibilitychange', refreshOnReturn)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', refreshOnReturn)
    }
  }, [user])
  
  const handleSignIn = (mode: 'sign-in' | 'sign-up' = 'sign-in') => {
    setAuthDialogMode(mode)
    setAuthDialogOpen(true)
  }
  
  const handleSignOut = async () => {
    const { signOut } = await import('@/lib/firebase')
    await signOut()
    window.location.href = '/'
  }
  
  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn: handleSignIn,
        signOut: handleSignOut,
      }}
    >
      {children}
      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} initialMode={authDialogMode} />
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = React.useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

